import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import * as React from "react";

import {
  getAdminModelMarkup,
  resetAdminModelMarkup,
  updateAdminModelMarkup,
} from "@ando/core";
import { Button, PencilCard, useToast } from "@ando/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/** The Models v2 tariff is separate from the existing Engine markup. */
export function ModelMarkupCard({ tenantId }: { tenantId?: string }): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ["admin", "pricing", "model-markup", tenantId ?? "global"] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => getAdminModelMarkup(tenantId),
    staleTime: 60_000,
    retry: false,
  });
  const [draft, setDraft] = React.useState("");
  React.useEffect(() => {
    if (query.data) setDraft(String(query.data.markup_pct));
  }, [query.data]);

  const amount = draft.trim() ? Number(draft) : NaN;
  const min = query.data?.min_pct ?? 0;
  const max = query.data?.max_pct ?? 500;
  const valid = Number.isFinite(amount) && amount >= min && amount <= max;
  const dirty = valid && amount !== query.data?.markup_pct;
  const mutation = useMutation({
    mutationFn: () => updateAdminModelMarkup(amount, tenantId),
    onSuccess: (value) => {
      queryClient.setQueryData(queryKey, value);
      toast.success(i18n._(msg`Tariffa modelli salvata`));
    },
    onError: (error) => {
      toast.error(i18n._(msg`Impossibile salvare la tariffa modelli`), {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
  const reset = useMutation({
    mutationFn: () => resetAdminModelMarkup(tenantId!),
    onSuccess: (value) => {
      queryClient.setQueryData(queryKey, value);
      toast.success(i18n._(msg`Tariffa globale ripristinata`));
    },
    onError: (error) => {
      toast.error(i18n._(msg`Impossibile ripristinare la tariffa globale`), {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  return (
    <PencilCard className="max-w-xl p-5">
      {query.isLoading ? (
        <div role="status" className="flex items-center gap-2 text-[12px] text-ink-3">
          <Loader2 size={14} className="animate-spin" aria-hidden />
          <Trans>Caricamento…</Trans>
        </div>
      ) : query.isError || !query.data || !Number.isFinite(query.data.markup_pct) ? (
        <p role="alert" className="text-[12px] text-destructive">
          <Trans>Impossibile caricare la tariffa modelli.</Trans>
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <label htmlFor={`model-markup-${tenantId ?? "global"}`} className="font-medium text-[12px] text-ink-1">
              <Trans>Commissione Ando sui modelli</Trans>
            </label>
            <span className="text-[11.5px] text-ink-3">
              {tenantId
                ? query.data?.is_override
                  ? <Trans>Tariffa specifica di questa organizzazione</Trans>
                  : <Trans>Tariffa globale ereditata</Trans>
                : <Trans>Tariffa globale predefinita</Trans>}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <input
              id={`model-markup-${tenantId ?? "global"}`}
              type="number"
              min={min}
              max={max}
              step="0.5"
              inputMode="decimal"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              aria-invalid={!valid}
              className="h-9 w-28 rounded-md border border-hairline bg-card px-2.5 text-right text-[13px] text-ink-1 tabular-nums focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
            />
            <span className="text-[13px] text-ink-2">%</span>
            <Button variant="primary" size="sm" disabled={!dirty || mutation.isPending || reset.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
              <Trans>Salva</Trans>
            </Button>
            {tenantId && query.data?.is_override ? (
              <Button variant="outline" size="sm" disabled={mutation.isPending || reset.isPending} onClick={() => reset.mutate()}>
                <Trans>Usa tariffa globale</Trans>
              </Button>
            ) : null}
          </div>
          {!valid && draft.trim() ? (
            <p className="text-[11.5px] text-destructive"><Trans>Inserisci una percentuale tra {min}% e {max}%.</Trans></p>
          ) : null}
          <p className="text-[11.5px] leading-relaxed text-ink-3">
            <Trans>
              100% significa una commissione Ando pari al costo di riferimento del modello.
              Con BYOK il provider fattura direttamente il cliente e Ando addebita questa commissione
              separatamente. Con la chiave Ando, Ando addebita costo del provider più commissione.
              La tariffa viene fissata quando la richiesta è registrata.
            </Trans>
          </p>
        </div>
      )}
    </PencilCard>
  );
}
